import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmContactsApi, crmOrgsApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import Modal from '../Modal';
import { invalidateCrm } from '../../lib/queryCache';
import type { CrmContact } from '../../types';

interface Props {
  contact?: CrmContact;
  onClose: () => void;
  onSaved: (id: string) => void;
  standalone?: boolean;
}

export default function ContactForm({ contact, onClose, onSaved, standalone }: Props) {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [form, setForm] = useState({
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    mobile: contact?.mobile ?? '',
    jobTitle: contact?.jobTitle ?? '',
    department: contact?.department ?? '',
    status: contact?.status ?? 'lead',
    source: contact?.source ?? '',
    organizationId: contact?.organizationId ?? '',
    linkedInUrl: contact?.linkedInUrl ?? '',
    notes: contact?.notes ?? '',
    tags: contact?.tags?.join(', ') ?? '',
  });
  const [error, setError] = useState('');

  const { data: orgs } = useQuery({ queryKey: ['crm-orgs-pick', cid], queryFn: () => crmOrgsApi.list({ limit: 200 }), enabled: !!cid });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        organizationId: form.organizationId || undefined,
      };
      return contact ? crmContactsApi.update(contact.id, payload) : crmContactsApi.create(payload);
    },
    onSuccess: (saved) => { invalidateCrm(queryClient); onSaved(saved.id); },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  });

  const content = (
    <div className="space-y-4">
      {error && <div className="text-[13px] text-err bg-primary-muted rounded-lg p-3">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">First Name *</label>
          <input className="input w-full" value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="First name" />
        </div>
        <div>
          <label className="label">Last Name</label>
          <input className="input w-full" value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Last name" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Email</label>
          <input className="input w-full" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
        </div>
        <div>
          <label className="label">Phone</label>
          <input className="input w-full" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 555 000 0000" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Mobile</label>
          <input className="input w-full" type="tel" value={form.mobile} onChange={e => set('mobile', e.target.value)} />
        </div>
        <div>
          <label className="label">Status</label>
          <select className="select w-full" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="lead">Lead</option>
            <option value="prospect">Prospect</option>
            <option value="customer">Customer</option>
            <option value="churned">Churned</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Job Title</label>
          <input className="input w-full" value={form.jobTitle} onChange={e => set('jobTitle', e.target.value)} />
        </div>
        <div>
          <label className="label">Department</label>
          <input className="input w-full" value={form.department} onChange={e => set('department', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Organization</label>
          <select className="select w-full" value={form.organizationId} onChange={e => set('organizationId', e.target.value)}>
            <option value="">— none —</option>
            {orgs?.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Source</label>
          <select className="select w-full" value={form.source} onChange={e => set('source', e.target.value)}>
            <option value="">— unknown —</option>
            <option value="website">Website</option>
            <option value="referral">Referral</option>
            <option value="cold_outreach">Cold Outreach</option>
            <option value="event">Event</option>
            <option value="social_media">Social Media</option>
            <option value="advertisement">Advertisement</option>
            <option value="email">Email</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="label">LinkedIn URL</label>
        <input className="input w-full" value={form.linkedInUrl} onChange={e => set('linkedInUrl', e.target.value)} placeholder="https://linkedin.com/in/…" />
      </div>

      <div>
        <label className="label">Tags <span className="text-muted font-normal">(comma-separated)</span></label>
        <input className="input w-full" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="vip, enterprise, hot-lead" />
      </div>

      <div>
        <label className="label">Notes</label>
        <textarea className="input w-full resize-y h-20" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes…" />
      </div>
    </div>
  );

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.firstName}>
        {saveMutation.isPending ? 'Saving…' : contact ? 'Save Changes' : 'Create Contact'}
      </button>
    </>
  );

  if (standalone) {
    return (
      <div className="max-w-2xl flex flex-col gap-6">
        <h1 className="page-title">{contact ? 'Edit Contact' : 'New Contact'}</h1>
        <div className="card">{content}</div>
        <div className="flex justify-end gap-2">{footer}</div>
      </div>
    );
  }

  return (
    <Modal open onClose={onClose} title={contact ? 'Edit Contact' : 'New Contact'} width={672} busy={saveMutation.isPending} footer={footer}>
      <div className="max-h-[70vh] overflow-y-auto pr-1">{content}</div>
    </Modal>
  );
}
